import { TransferStatusBadge } from '@/components/badges/TransferStatusBadge'
import { SectionCard } from '@/components/SectionCard'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { getProductName } from '@/features/equipment/utils/product'
import { HandleTransferDialog } from '@/features/requests/components/HandleTransferDialog'
import { useGetTransferRequests } from '@/features/requests/hooks/useTransferRequest'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useUI } from '@/hooks/useUI'
import { CalendarDays, History, MapPin, Package, User } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export function RequestsHistoryView() {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedTransferId, setSelectedTransferId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const { transferStatuses } = useUI()
  const { formatDateTime } = useDateFormatter()

  const statusParam = statusFilter === 'all' ? undefined : statusFilter
  const { data: transfers = [], isLoading } = useGetTransferRequests({
    status: statusParam,
  })

  return (
    <>
      <SectionCard
        data-testid="transfers-history-list"
        title={t('requests.historyTitle')}
        right={
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="transfers-history-status-filter" className="h-8 w-[140px] bg-secondary/50 text-xs">
              <SelectValue placeholder={t('common.status')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              <SelectItem value="all">{t('common.allStatuses')}</SelectItem>
              {transferStatuses?.map(status => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      >
        <div className="flex flex-col gap-3">
          {isLoading ? (
            <div className="flex flex-col items-center py-12 text-center">
              <History className="size-10 text-muted-foreground/30" />
              <p className="mt-3 text-xs text-muted-foreground">{t('common.loading')}</p>
            </div>
          ) : (
            transfers.map(tr => {
              const displayDate = tr.completedAt ?? tr.scheduledDate

              return (
                <div
                  key={tr.id}
                  data-testid="transfer-history-card"
                  className="flex cursor-pointer flex-col gap-2 rounded-lg border border-border/30 bg-secondary/20 p-4 transition-colors hover:border-primary/30"
                  onClick={() => {
                    setSelectedTransferId(tr.id)
                    setDetailOpen(true)
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">
                          {getProductName(tr.productName, tr.productBrand, tr.productModel) || '-'}
                        </span>
                        <TransferStatusBadge status={tr.status} />
                      </div>
                      {tr.reason && <span className="text-xs text-muted-foreground">{tr.reason}</span>}
                    </div>
                    {displayDate && (
                      <span className="text-[10px] text-muted-foreground">
                        {formatDateTime(displayDate, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
                    <span>{t('requests.from')}:</span>
                    <span className="flex items-center gap-1">
                      <Package className="size-2.5" />
                      {tr.sourceDepartmentName || '-'}
                    </span>
                    <span>{t('requests.requester')}:</span>
                    <span className="flex items-center gap-1">
                      <User className="size-2.5" />
                      {tr.requesterName || '-'}
                    </span>
                    {tr.scheduledDate && (
                      <span className="flex items-center gap-1">
                        <CalendarDays className="size-2.5" />
                        {formatDateTime(tr.scheduledDate, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
                    <span>{t('requests.to')}:</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="size-2.5" />
                      {tr.targetDepartmentName || '-'}
                    </span>
                    {tr.completedAt && (
                      <span className="flex items-center gap-1">
                        <CalendarDays className="size-2.5" />
                        {formatDateTime(tr.completedAt, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                </div>
              )
            })
          )}
          {!isLoading && transfers.length === 0 && (
            <div className="flex flex-col items-center py-12 text-center">
              <History className="size-10 text-muted-foreground/30" />
              <p className="mt-3 text-xs text-muted-foreground">{t('requests.noHistory')}</p>
            </div>
          )}
        </div>
      </SectionCard>

      <HandleTransferDialog transferId={selectedTransferId} open={detailOpen} onOpenChange={setDetailOpen} />
    </>
  )
}
